/**
 * The live specimens behind the record entries (componentCatalog.records).
 * Kept apart so that file holds only data, as componentCatalog.harness does
 * for the ds/ family.
 */
import { useMemo, useState } from 'react';
import { Handshake } from 'lucide-react';
import { DetailPageFrame, DetailRail } from '../../components/detail-page/DetailPageFrame';
import { DestinationQuestion, OwnedSectionHeading } from '../../components/detail-page/detailPageParts';
import { ListTiles } from '../../components/collection/ListTiles';
import { EntityListPage, type EntityListTile } from '../../components/collection/EntityListPage';
import type { TableColumnWithRender } from '../../components/collection/types';
import { MasterDetailShell } from '../../components/master-detail/MasterDetailShell';
import { LABEL_MONO, btnSecondary, status } from '../../components/mono/primitives';

export function TilesDemo() {
  const [active, setActive] = useState<string | null>('active');
  return (
    <div className="w-full max-w-3xl">
      <ListTiles
        activeId={active}
        onSelect={setActive}
        tiles={[
          { id: 'all', label: 'All commitments', value: 12, filters: false },
          { id: 'active', label: 'Active', value: 9, tone: 'green' },
          { id: 'uncovered', label: 'Uncovered', value: 3, tone: 'coral', hint: 'Active, with no linked item in an active cycle' },
          { id: 'due-soon', label: 'Due in 30 days', value: 2, tone: 'mustard' },
        ]}
      />
    </div>
  );
}

export function FrameDemo() {
  const [tab, setTab] = useState<'overview' | 'history'>('overview');
  const [open, setOpen] = useState(false);
  return (
    <div className="w-full">
      <DetailPageFrame<'overview' | 'history'>
        className=""
        back={{ label: 'Back to commitments' }}
        identity={
          <>
            <span {...status('green')}>Active</span>
            <span {...status('coral')}>Critical</span>
          </>
        }
        actions={<button {...btnSecondary()}>Delete</button>}
        title="SSO for Acme renewal"
        description="Acme renews only if single sign-on ships before the renewal date."
        standing={[
          { label: 'Coverage', value: 'Covered', note: '1 of 1 linked item in an active cycle.' },
          { label: 'Assignments', value: 2, note: 'binding' },
          { label: 'Due', value: 'Nov 14, 2026', note: 'Due in 12 days' },
        ]}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'history', label: 'History' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        tabsLabel="Example destinations"
        rail={
          <DetailRail
            groups={[
              {
                h: 'At a glance',
                rows: [
                  { k: 'Priority', v: 'Critical', onClick: () => setOpen((o) => !o), open, editor: <div className="text-sm text-[var(--text-sec)]">An editor opens here, in place.</div> },
                  { k: 'Owner', v: 'Pat Lee' },
                ],
              },
            ]}
          />
        }
      >
        <DestinationQuestion>What was promised, and is the work behind it on the plan?</DestinationQuestion>
        <OwnedSectionHeading title="Linked items · 1" owned="the work that keeps this promise" />
        <p className="py-2.5 text-[var(--text-sec)]">Rebuild onboarding</p>
      </DetailPageFrame>
    </div>
  );
}


/* ── The list, live: sample rows in the real shell ─────────────────────── */

interface SampleRow {
  id: string;
  title: string;
  counterparty: string;
  status: 'active' | 'fulfilled';
  priority: 'critical' | 'high' | 'medium' | 'low';
  due: string;
  covered: boolean;
}

const SAMPLE_ROWS: SampleRow[] = [
  { id: 's1', title: 'SSO for Acme renewal', counterparty: 'Acme Corp', status: 'active', priority: 'critical', due: 'Nov 14', covered: true },
  { id: 's2', title: 'SOC 2 Type II report', counterparty: 'External auditor', status: 'active', priority: 'high', due: 'Jan 31', covered: false },
  { id: 's3', title: 'Usage billing for Globex', counterparty: 'Globex', status: 'active', priority: 'high', due: 'Nov 27', covered: true },
  { id: 's4', title: 'Partner API v2', counterparty: 'Initech', status: 'active', priority: 'medium', due: 'Jan 1', covered: false },
  { id: 's5', title: 'GDPR data export', counterparty: 'EU regulators', status: 'active', priority: 'critical', due: 'Oct 30', covered: false },
  { id: 's6', title: 'Cold start under 2 seconds', counterparty: 'Customers at large', status: 'fulfilled', priority: 'medium', due: 'Sep 23', covered: true },
  { id: 's7', title: 'Accessibility audit fixes', counterparty: 'State procurement', status: 'active', priority: 'high', due: 'Nov 7', covered: true },
  { id: 's8', title: 'Quarterly roadmap review', counterparty: 'Advisory board', status: 'active', priority: 'low', due: 'Nov 30', covered: false },
  { id: 's9', title: 'Uptime 99.9 for Umbrella', counterparty: 'Umbrella', status: 'active', priority: 'low', due: '—', covered: true },
];

const SAMPLE_TILES: EntityListTile<SampleRow>[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active', match: (r) => r.status === 'active', tone: 'green' },
  { id: 'uncovered', label: 'Uncovered', match: (r) => r.status === 'active' && !r.covered, tone: (n) => (n > 0 ? 'coral' : 'quiet') },
  { id: 'critical', label: 'Critical', match: (r) => r.priority === 'critical', tone: (n) => (n > 0 ? 'mustard' : 'quiet') },
];

const PRIORITY_TONE = { critical: 'coral', high: 'mustard', medium: 'mustard', low: 'neutral' } as const;

export function ListDemo() {
  const columns = useMemo((): TableColumnWithRender<SampleRow>[] => [
    {
      key: 'title',
      label: 'Commitment',
      sortable: true,
      minWidth: 220,
      flex: true,
      render: (r) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium text-[var(--text-pri)]">{r.title}</span>
          <span className="block truncate text-xs text-[var(--text-ter)]">{r.counterparty}</span>
        </div>
      ),
    },
    { key: 'status', label: 'Status', sortable: true, minWidth: 100, render: (r) => <span {...status(r.status === 'active' ? 'green' : 'slate')}>{r.status}</span> },
    { key: 'priority', label: 'Priority', sortable: true, minWidth: 100, render: (r) => <span {...status(PRIORITY_TONE[r.priority])}>{r.priority}</span> },
    { key: 'due', label: 'Due', minWidth: 90, render: (r) => <span className="text-xs text-[var(--text-sec)]">{r.due}</span> },
    { key: 'covered', label: 'Coverage', minWidth: 110, render: (r) => <span {...status(r.covered ? 'green' : 'coral')}>{r.covered ? 'Covered' : 'At risk'}</span> },
  ], []);
  return (
    <div className="w-full">
      <EntityListPage<SampleRow, string>
        items={SAMPLE_ROWS}
        getId={(r) => r.id}
        masterDetail={false}
        title="Commitment Registry"
        subtitle="Sample rows. Press a tile, open a row's chevron, sort a column."
        icon={<Handshake className="h-4 w-4 text-[var(--text-ter)]" />}
        itemLabel="commitment"
        searchFields={['title', 'counterparty']}
        sortOptions={[{ field: 'title', label: 'Title' }, { field: 'priority', label: 'Priority' }]}
        defaultSort={{ field: 'title', direction: 'asc' }}
        groupOptions={[{ field: 'status', label: 'Status' }, { field: 'priority', label: 'Priority' }]}
        tableColumns={columns}
        viewModes={['table']}
        selectable={false}
        tiles={SAMPLE_TILES}
        renderPeek={(r) => (
          <div className="space-y-2 text-sm">
            <div className="text-[var(--text-sec)]">
              Made to {r.counterparty}, due {r.due}. {r.covered ? 'Its linked items are in an active cycle.' : 'No linked item is in an active cycle.'}
            </div>
            <button {...btnSecondary()}>Open this commitment&apos;s page</button>
          </div>
        )}
        emptyTitle="No commitments yet"
        emptyDescription="Record a promise, then link the items that keep it."
        className=""
        shellClassName="h-[560px]"
      />
    </div>
  );
}

/* ── The Inbox view: a queue worked one at a time ──────────────────────── */

const SAMPLE_ASKS = [
  { id: 'a1', from: 'Pat Lee', what: 'Accept: deliver SSO before the Acme renewal', when: '2h ago' },
  { id: 'a2', from: 'Sam Ortiz', what: 'Counteroffer on the SOC 2 evidence date', when: 'Yesterday' },
  { id: 'a3', from: 'Ana Ruiz', what: 'Report done: Partner API v2 endpoints', when: 'Mon' },
];

export function InboxDemo() {
  const [selected, setSelected] = useState<string | null>('a1');
  const ask = SAMPLE_ASKS.find((a) => a.id === selected) ?? null;
  return (
    <div className="w-full border" style={{ borderColor: 'var(--rule)' }}>
      <MasterDetailShell
        height={300}
        defaultMasterWidth={300}
        syncToUrl={false}
        widthStorageKey="catalog-inbox-width"
        selectedId={selected}
        onSelectionChange={setSelected}
        master={
          <ul>
            {SAMPLE_ASKS.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => setSelected(a.id)}
                  className={`block w-full border-b px-4 py-3 text-left ${selected === a.id ? 'bg-[var(--slate-dim)]' : 'hover:bg-[var(--hover-tint)]'}`}
                  style={{ borderColor: 'var(--rule)' }}
                >
                  <div className="text-sm font-medium text-[var(--text-pri)]">{a.what}</div>
                  <div className="text-xs text-[var(--text-ter)]">
                    {a.from} · {a.when}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        }
        detail={
          ask && (
            <div className="space-y-3 p-5">
              <div className={LABEL_MONO}>Asked by {ask.from}</div>
              <div className="text-lg font-semibold text-[var(--text-pri)]">{ask.what}</div>
              <div className="flex gap-2">
                <button {...btnSecondary()}>Answer</button>
                <button {...btnSecondary()}>Open the commitment</button>
              </div>
            </div>
          )
        }
      />
    </div>
  );
}
